import { z } from 'zod';
import { IntegrationProvider, SocialNetwork } from '../enums';

export const connectMetaSchema = z.object({
  /** Token de System User do Business Portfolio da agência (modelo agência). */
  systemUserToken: z.string().min(20).max(1000).optional(),
  /** Alternativa SaaS: código do Facebook Login for Business. */
  oauthCode: z.string().min(10).max(2000).optional(),
  businessId: z.string().max(64).optional(),
});
export type ConnectMetaInput = z.infer<typeof connectMetaSchema>;

/** ID numérico opcional que trata "" (campo vazio no form) como ausente. */
const optionalId = z.preprocess(
  (v) => (v === '' || v === null ? undefined : v),
  z.string().regex(/^\d+$/).optional(),
);

export const linkMetaAssetsSchema = z.object({
  adAccountId: z.string().regex(/^act_\d+$/, 'Use o formato act_<id>'),
  pageId: optionalId,
  instagramUserId: optionalId,
  pixelId: optionalId,
  datasetId: optionalId,
  catalogId: optionalId,
  leadFormIds: z.array(z.string().regex(/^\d+$/)).max(50).default([]),
});
export type LinkMetaAssetsInput = z.infer<typeof linkMetaAssetsSchema>;

export const linkGoogleAdsSchema = z.object({
  /** customer id sem hífens */
  customerId: z.string().regex(/^\d{10}$/, 'Use 10 dígitos sem hífen'),
  loginCustomerId: z.string().regex(/^\d{10}$/).optional(),
  descriptiveName: z.string().max(160).optional(),
  currencyCode: z.string().length(3).default('BRL'),
  timeZone: z.string().max(60).default('America/Sao_Paulo'),
  conversionActionIds: z.array(z.string().max(40)).max(50).default([]),
});
export type LinkGoogleAdsInput = z.infer<typeof linkGoogleAdsSchema>;

export const googleOauthCallbackSchema = z.object({
  code: z.string().min(10),
  state: z.string().min(10),
});

export const connectSocialAccountSchema = z.object({
  network: z.nativeEnum(SocialNetwork),
  externalId: z.string().min(1).max(120),
  username: z.string().max(160).optional(),
  displayName: z.string().max(200).optional(),
  /** Página do Facebook vinculada (obrigatória para IG). */
  pageId: z.string().max(120).optional(),
  /** URN da organização no LinkedIn: urn:li:organization:123 */
  organizationUrn: z.string().max(200).optional(),
  accessToken: z.string().min(10).max(5000),
  refreshToken: z.string().max(5000).optional(),
  expiresAt: z.coerce.date().optional(),
  scopes: z.array(z.string().max(80)).max(50).default([]),
});
export type ConnectSocialAccountInput = z.infer<typeof connectSocialAccountSchema>;

export const siteConnectorSchema = z.object({
  label: z.string().trim().min(2).max(160),
  baseUrl: z
    .string()
    .url()
    .refine((u) => u.startsWith('https://'), 'O conector exige HTTPS'),
  siteIdentifier: z
    .string()
    .trim()
    .min(3)
    .max(80)
    .regex(/^[a-z0-9-]+$/),
  /** Gerado pela plataforma se ausente; armazenado criptografado. */
  secret: z.string().min(32).max(200).optional(),
  ipAllowlist: z.array(z.string().max(64)).max(20).default([]),
  isActive: z.boolean().default(true),
});
export type SiteConnectorInput = z.infer<typeof siteConnectorSchema>;

export interface IntegrationHealth {
  provider: IntegrationProvider;
  status: string;
  lastCheckedAt: string | null;
  lastErrorMessage: string | null;
  expiresAt: string | null;
  scopes: string[];
}
